import { useRef, useEffect, type KeyboardEvent, type ClipboardEvent } from 'react';
import { useMfa } from '@/hooks/useMfa';
import { useLocalization } from '@/hooks/useLocalization';
import { useTenantStore } from '@/store/tenantStore';
import { showToast } from '@/utils/globalToast';
import { useOnboardingAnalytics } from './hooks/useOnboardingAnalytics';
import { AuthLayout } from './components/AuthLayout';
import { AuthLogo } from './components/AuthLogo';
import { authControlStyles as controls } from './components/AuthControls';
import { WarningDialog } from './components/WarningDialog';
import styles from './MfaPage.module.css';

/**
 * MfaPage — email-based Multi-Factor Authentication page.
 *
 * Mirrors Flutter WebMfaVerificationPage + WebMfaForms + WebEmailBasedMfaOtpWidget, with the
 * VerifyOtpScreenConstants / WebMfaPageConstants texts:
 *   1. Auto-send email OTP on mount (after 1s delay); a spinner until it is sent
 *   2. 6-digit OTP input, expiry countdown, resend cooldown
 *   3. Verify OTP → JWT validation → "User authenticated successfully" dialog → destination
 *   4. Too many OTP requests → "Error" dialog → logout → /login
 * Failures show Flutter's message as a toast (customAlertMsgNotifier: 15 s for long messages,
 * else 4 s).
 *
 * Layout as in Flutter: the onboarding panel (desktop) and a white column at least the screen
 * (and 600px) high with the OTP form centered in it: the auth logo, the email OTP illustration,
 * "Verify Email OTP", the masked email, the PinCodeTextField boxes, the timers, and the Logout /
 * Verify buttons. The row behind is primaryColor unless enableOnboardingBackgroundColor is false.
 *
 * Flutter source files:
 *   - presentation/web/auth/web_mfa_verification/web_mfa_verification_page.dart
 *   - presentation/web/auth/web_mfa_verification/web_mfa_forms.dart
 *   - presentation/web/auth/web_mfa_verification/widgets/web_email_based_mfa_otp_widget.dart
 */

/** The OTP expiry: minutes and seconds zero-padded */
function formatExpiry(totalSeconds: number): string {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

/** The resend countdown: Flutter's '0$minutes:$seconds', seconds not zero-padded ("00:9") */
function formatCooldown(totalSeconds: number): string {
  return `0${Math.floor(totalSeconds / 60)}:${totalSeconds % 60}`;
}

export function MfaPage() {
  const { t } = useLocalization();
  const { preferences } = useTenantStore();
  const mfa = useMfa();
  useOnboardingAnalytics();

  // OTP input refs for individual digit boxes
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Focus first input when OTP step is shown
  useEffect(() => {
    if (mfa.step === 'otp' && mfa.otpSent) {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [mfa.step, mfa.otpSent]);

  // GenericHelpers.customAlertMsgNotifier(durationInSecond: message.length > 75 ? 15 : 4)
  useEffect(() => {
    if (mfa.error) showToast(mfa.error, 'error', mfa.error.length > 75 ? 15000 : 4000);
  }, [mfa.error]);

  const fillRow =
    (preferences as Record<string, unknown> | null)?.['enableOnboardingBackgroundColor'] !== false;

  // ── OTP Input Handlers ─────────────────────────────────────────

  function handleDigitChange(index: number, value: string) {
    if (!/^\d?$/.test(value)) return;

    const currentOtp = mfa.otp.split('');
    while (currentOtp.length < mfa.otpLength) currentOtp.push('');
    currentOtp[index] = value;
    mfa.setOtp(currentOtp.join(''));

    if (value && index < mfa.otpLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !mfa.otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter' && mfa.canVerify) {
      mfa.handleVerifyOtp();
    }
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, mfa.otpLength);
    if (pasted.length > 0) {
      mfa.setOtp(pasted);
      const focusIndex = Math.min(pasted.length, mfa.otpLength - 1);
      inputRefs.current[focusIndex]?.focus();
    }
  }

  // ── Render ─────────────────────────────────────────────────────

  return (
    <AuthLayout fillRow={fillRow} formColumnClassName={styles.formColumn}>
      <div className={styles.forms} aria-busy={mfa.step === 'loading' || mfa.isLoading || undefined}>
        {/* ── Sending the first OTP — WebMfaForms shows only a progress indicator ── */}
        {mfa.step === 'loading' && <div className={styles.spinner} role="progressbar" />}

        {/* ── OTP Input Step (WebEmailBasedMfaOtpWidget) ── */}
        {mfa.step !== 'loading' && (
          <div className={styles.otpWidget}>
            <AuthLogo />

            <img src="/assets/icons/email_otp_image.svg" alt="" className={styles.illustration} />

            <h1 className={styles.title}>{t('Verify Email OTP')}</h1>

            <p className={styles.instruction}>
              {`${t('An OTP has been sent to your registered email address')} `}
              <span className={styles.maskedEmail}>{`${mfa.maskedEmail}.`}</span>
              {` ${t('Please enter the OTP to proceed.')}`}
            </p>

            <div className={styles.otpRow}>
              {Array.from({ length: mfa.otpLength }).map((_, i) => (
                <input
                  key={i}
                  ref={(el) => { inputRefs.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={mfa.otp[i] ?? ''}
                  onChange={(e) => handleDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  onPaste={i === 0 ? handlePaste : undefined}
                  disabled={mfa.isLoading}
                  autoComplete="one-time-code"
                  aria-label={`${t('Enter OTP')} ${i + 1}`}
                  className={styles.otpInput}
                />
              ))}
            </div>

            {mfa.showOtpExpiry && (
              <div className={styles.expiry}>
                {mfa.otpExpiresIn <= 0
                  ? t('OTP expired, Please Resend again.')
                  : `${t('OTP expires in')} ${formatExpiry(mfa.otpExpiresIn)} ${t('mins')}`}
              </div>
            )}

            <div className={styles.resendRow}>
              <span className={styles.resendText}>
                {t("Didn't receive code?")}
                <button
                  type="button"
                  className={styles.resendBtn}
                  onClick={mfa.canResend ? mfa.handleSendOtp : undefined}
                  disabled={!mfa.canResend}
                >
                  {t(' Resend OTP')}
                </button>
              </span>
              {mfa.resendCooldown > 0 && (
                <span className={styles.cooldown}>{formatCooldown(mfa.resendCooldown)}</span>
              )}
            </div>

            <div className={styles.actions}>
              <button
                type="button"
                className={`${controls.customButton} ${controls.customButtonAccent}`}
                onClick={mfa.handleLogout}
                disabled={mfa.isLoading}
              >
                {t('Logout')}
              </button>
              <button
                type="button"
                className={controls.customButton}
                onClick={mfa.handleVerifyOtp}
                disabled={!mfa.canVerify}
              >
                {t('Verify')}
              </button>
            </div>

            {/* While sending / verifying — Flutter's centered CircularProgressIndicator */}
            {mfa.isLoading && <div className={`${styles.spinner} ${styles.busySpinner}`} role="progressbar" />}
          </div>
        )}
      </div>

      {/* Success — Flutter CustomDialog.showWarningDialog(Constants.authenticatedSuccessMsg) */}
      {mfa.step === 'success' && (
        <WarningDialog
          content="User authenticated successfully"
          btnText="Continue"
          onOk={mfa.handleAuthenticatedContinue}
        />
      )}

      {/* 429 on send — Flutter CustomDialog.showWarningDialog(title: 'Error', ErrorConstants.tooManyRequestsError) */}
      {mfa.showTooManyRequests && (
        <WarningDialog
          title="Error"
          content="Too many requests. Please try again after some time."
          onOk={mfa.handleTooManyRequestsOk}
        />
      )}
    </AuthLayout>
  );
}
